/**
 * SCWT-PM-V2 Phase 7 · Slice E — Landlord invitation acceptance.
 *
 * Route: /landlord/water-health/invitations/:token
 *
 * The token in the URL is unauthenticated — we resolve it through
 * the landlord-portal endpoint AFTER the landlord has logged in
 * (magic-link session). Any invalid / expired / used / revoked
 * token returns a generic message — we never leak whether an
 * account or invitation exists.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, CheckCircle2, Info } from "lucide-react";
import { centsToAud, fmtDateAU } from "@/lib/lwhStatus";
import { useFeature } from "@/lib/useFeature";

const base = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${base}/api`, withCredentials: true });

export default function LandlordWaterHealthInvitation() {
  const { token } = useParams();
  const [params]  = useSearchParams();
  const navigate  = useNavigate();
  const { isVisible: flagOn, loading: flagLoading } = useFeature("landlord_water_health_program");
  const [preview, setPreview]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [consent, setConsent]   = useState(false);
  const [error, setError]       = useState(null);
  const [submitting, setSubmit] = useState(false);

  useEffect(() => {
    if (!flagOn || !token) return;
    (async () => {
      try {
        await api.get("/landlord-portal/me");
      } catch (e) {
        // Store the invitation path so post-login redirect brings them back.
        sessionStorage.setItem("post_login_return_to", window.location.pathname);
        navigate("/landlord/login");
        return;
      }
      try {
        // Fetch pricing so we can show the amount + terms — the
        // invitation-specific details are only surfaced after accept.
        const r = await api.get("/landlord-portal/water-health/eligibility?property_id=___preview___");
        // We only render the pricing block here; full property-level
        // detail comes back after successful accept as the checkout
        // flow starts.
        setPreview(r.data);
      } catch {
        setPreview({ pricing: { amount_cents: 7900, currency: "AUD",
                                member_discount_pct: 10, stripe_configured: true } });
      } finally { setLoading(false); }
    })();
  }, [flagOn, token, navigate]);

  const accept = async () => {
    setSubmit(true); setError(null);
    try {
      const { data } = await api.post(
        `/landlord-portal/water-health/invitations/${encodeURIComponent(token)}/accept`,
        { property_id: "", origin_url: window.location.origin, consent_confirmed: true },
      );
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      // Generic safe message per Phase 7 rule — never leak whether
      // the token is invalid / expired / revoked / already used.
      setError("This invitation link is no longer valid. Please ask your Property Manager to send a new one.");
      setSubmit(false);
    }
  };

  if (flagLoading || loading) {
    return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  }
  if (!flagOn) {
    return <div className="min-h-screen grid place-items-center p-6 text-center text-slate-600 max-w-md mx-auto">
      This enrolment link is not currently available.
    </div>;
  }
  const price = preview?.pricing || {};
  return (
    <div className="min-h-screen bg-slate-50">
      <div data-testid="lwh-invitation-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-700 mb-2">
          <ShieldCheck className="w-3.5 h-3.5" /> Water Health Program invitation
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
          Enrol your property
        </h1>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          Your Property Manager has invited you to enrol this property in the
          AWARE Water Health Program. Review the terms below and confirm to
          continue to secure payment.
        </p>

        <div className="mt-6 rounded-3xl bg-white border border-slate-200 p-5 sm:p-6 shadow-sm">
          <div className="flex items-baseline gap-2">
            <span data-testid="lwh-invitation-price"
                   className="text-3xl font-bold text-slate-900">
              {centsToAud(price.amount_cents || 7900)}
            </span>
            <span className="text-sm text-slate-500">per property per year</span>
          </div>
          <p className="mt-1.5 text-sm text-slate-600">
            Laboratory tests are charged separately when a retest is due.
          </p>
          <div className="mt-5 grid gap-2 text-sm">
            {["Annual retest reminders + pre-filled booking link",
               "Water Health Record and test history for this property",
               "Priority booking support",
               `${price.member_discount_pct || 10}% member discount on eligible retail lab tests for this property`].map((b, i) => (
              <div key={i} className="flex items-start gap-2 text-slate-800">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>{b}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4 text-[13px] text-slate-700 leading-relaxed">
            Renews automatically. Cancel any time — benefits continue until
            your paid-through date. Your card details are never shared with
            your Property Manager or your local PWS Partner. Report access
            remains governed by your existing sharing permissions.
          </div>
        </div>

        {error && (
          <div data-testid="lwh-invitation-error"
                className="mt-4 rounded-xl bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800">
            {error}
          </div>
        )}

        <label className="mt-5 flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="lwh-invitation-consent"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-sm text-slate-800">
            I confirm I want to enrol this property and understand the annual
            fee auto-renews.
          </span>
        </label>

        <button
          type="button"
          data-testid="lwh-invitation-submit"
          disabled={!consent || submitting}
          onClick={accept}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white font-semibold py-3 shadow-sm"
        >
          {submitting ? "Preparing checkout…" : "Accept invitation and pay"}
        </button>
      </div>
    </div>
  );
}
